import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Container, Button, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CompanyList from '../components/CompanyList';
import { getTaskResult, getSearchResultsByTaskId } from '../services/api';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../contexts/AuthContext';

const ResultsPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { updateCompanies, companies } = useCompany();
  const storedHistoryTaskId = sessionStorage.getItem('viewing_history_task_id');

  const fromHistory = (searchParams.get('from') || '').toLowerCase() === 'history'
    || storedHistoryTaskId === taskId;

  const BackButton = () => (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate(fromHistory ? '/search-history' : '/')}
      sx={{ mb: 2 }}
    >
      {fromHistory ? 'Вернуться к истории поиска' : 'Вернуться к поиску'}
    </Button>
  );

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [emptyResult, setEmptyResult] = useState(false);

  const loadCompletedResults = useCallback(async () => {
    // getTaskResult первым — он возвращает полный MinIO JSON со всеми полями компаний
    // getSearchResultsByTaskId как fallback — может отдавать урезанные данные
    const fetchers = [getTaskResult, getSearchResultsByTaskId];

    for (const fetcher of fetchers) {
      try {
        const response = await fetcher(taskId);
        if (!response) continue;

        let companiesList = null;
        let total = 0;
        let processingTime = 0;
        let analysisStats = {};
        let city = 'Результаты поиска';

        if (response?.results && Array.isArray(response.results)) {
          companiesList = response.results;
          total = response.total || companiesList.length;
          processingTime = response.processing_time_ms || 0;
          analysisStats = response.analysis_statistics || {};
          city = response.city || city;
        } else if (response?.companies && Array.isArray(response.companies)) {
          companiesList = response.companies;
          total = response.total_companies || companiesList.length;
          processingTime = response.processing_time_ms || 0;
          analysisStats = response.analysis_statistics || {};
          city = response.city || city;
        } else if (Array.isArray(response) && response.length > 0) {
          companiesList = response;
          total = response.length;
        }

        // Если компании не распознаны в этом источнике — пробуем следующий
        if (companiesList === null) continue;

        // Если этот источник вернул 0 компаний — пробуем следующий (fallback может иметь данные)
        if (companiesList.length === 0) continue;

        const companiesWithAnalysis = companiesList.filter(c => c.analysis || c.gemini_analysis).length;
        const successfulAnalysis = companiesList.filter(c => {
          const analysis = c.analysis || c.gemini_analysis;
          return analysis && analysis.categories && analysis.categories.length > 0;
        }).length;

        const metadata = {
          total: total || companiesList.length,
          processing_time_ms: processingTime || 0,
          analysis_statistics: {
            total: companiesList.length,
            analyzed: companiesWithAnalysis,
            successful: successfulAnalysis,
            success_rate: (successfulAnalysis / companiesList.length) * 100
          },
          task_id: taskId,
          from_history: true
        };

        updateCompanies(companiesList, city, metadata);
        setShowResults(true);

        if (storedHistoryTaskId === taskId) {
          sessionStorage.removeItem('viewing_history_task_id');
        }

        return true;
      } catch (err) {
        if (err.response?.status === 404) continue;
        console.error('Error loading completed results:', err);
        return false;
      }
    }

    return false;
  }, [storedHistoryTaskId, taskId, updateCompanies]);

  useEffect(() => {
    if (authLoading || !user || !taskId || showResults) return;

    const initializeResults = async () => {
      setLoading(true);
      setError('');
      setEmptyResult(false);

      const loaded = await loadCompletedResults();

      if (!loaded) {
        setError('Результаты не найдены. Возможно, задача ещё выполняется или была удалена.');
      }

      setLoading(false);
    };

    initializeResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, authLoading, user]);

  // Отдельно следим: результаты загружены, но компаний нет
  useEffect(() => {
    if (showResults && companies !== undefined) {
      setEmptyResult(companies.length === 0);
    }
  }, [showResults, companies]);

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <BackButton />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && !showResults && (
        <Typography variant="h4" component="h1" gutterBottom>
          Загрузка результатов...
        </Typography>
      )}

      {showResults && !emptyResult && companies && companies.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
            Результаты анализа
          </Typography>
          <CompanyList />
        </Box>
      )}

      {showResults && emptyResult && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          По данному запросу компании не найдены. Попробуйте другой город или увеличьте количество организаций.
        </Alert>
      )}
    </Container>
  );
};

export default ResultsPage;
